<template>
  <!-- Executive Header & Control Bar -->
  <div
    :class="[
      'bg-white dark:bg-[#111C30] rounded-2xl border border-slate-200/90 dark:border-[#263449] p-4 md:p-5 mb-6 shadow-xs transition-all duration-200',
      TotalStudents === 0 ? 'mt-8' : '',
    ]"
  >
    <div
      class="flex flex-col-reverse lg:flex-row gap-4 items-stretch lg:items-center justify-between"
    >
      <!-- Search and Filters Section -->
      <div
        class="flex flex-col sm:flex-row gap-3 md:gap-4 flex-1 w-full lg:w-auto"
      >
        <!-- Search Input -->
        <div class="flex-1 min-w-0">
          <Search
            @search-text="emitSearchText"
            v-model="searchText"
          />
        </div>

        <!-- Filters Container -->
        <div class="flex flex-col sm:flex-row gap-2.5 sm:gap-3 items-stretch sm:items-center">
          <div
            v-if="isPassoutClass"
            class="min-w-0"
          >
            <YearPicker @selected-year="emitSelectedYear" />
          </div>
          <div class="min-w-0">
            <StatusFilter @selected-class="emitSelectedClass" />
          </div>
        </div>
      </div>

      <!-- Add Student Primary Button -->
      <button
        @click="showAddModal = true"
        class="inline-flex items-center justify-center gap-2 px-4 py-2.5 bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl text-xs sm:text-sm font-semibold shadow-xs transition-colors shrink-0 cursor-pointer"
      >
        <Plus class="w-4 h-4" />
        <span>Add Student</span>
      </button>
    </div>
  </div>

  <!-- Modal: Add New Student -->
  <div
    v-if="showAddModal"
    class="fixed inset-0 z-[10000] bg-slate-950/70 backdrop-blur-md flex items-center justify-center p-3 sm:p-6 overflow-y-auto"
    @click.self="closeAddModal"
  >
    <div
      class="bg-white dark:bg-[#111C30] w-full max-w-4xl max-h-[92vh] rounded-2xl sm:rounded-3xl border border-slate-200/90 dark:border-[#263449] relative animate-fade-in flex flex-col shadow-2xl overflow-hidden"
    >
      <!-- Modal Loading Overlay -->
      <AppLoader
        :isVisible="isAdding"
        variant="card"
        size="md"
        message="Enrolling Student..."
        subtext="Creating student profile, credentials, and class registry"
        color="indigo"
      />

      <!-- Top Subtle Gradient Accent Line -->
      <div class="h-1 bg-gradient-to-r from-blue-600 via-indigo-600 to-violet-600 shrink-0"></div>

      <!-- Professional Modal Header Bar -->
      <div
        class="bg-white dark:bg-[#111C30] px-6 py-4 border-b border-slate-100 dark:border-[#263449] flex items-center justify-between shrink-0"
      >
        <div>
          <h2 class="text-base sm:text-lg font-bold text-slate-900 dark:text-white tracking-tight">
            Enroll New Student
          </h2>
          <p class="text-xs text-slate-400 dark:text-slate-500 mt-0.5 font-normal">
            Register student profile, class admission, and verification files
          </p>
        </div>

        <button
          @click="closeAddModal"
          :disabled="isAdding"
          class="w-8 h-8 rounded-xl flex items-center justify-center text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 hover:bg-slate-100 dark:hover:bg-[#18243A] transition-colors disabled:opacity-50"
          title="Close"
        >
          <X class="w-4 h-4" />
        </button>
      </div>

      <!-- Navigation Segmented Tabs -->
      <div
        class="px-6 py-2 bg-slate-50/80 dark:bg-[#0B1120]/40 border-b border-slate-100 dark:border-[#263449] flex items-center gap-1.5 overflow-x-auto shrink-0"
      >
        <button
          type="button"
          v-for="tab in formTabs"
          :key="tab.id"
          @click="activeTab = tab.id"
          :class="[
            'px-3 py-1.5 rounded-lg text-xs font-semibold whitespace-nowrap transition-all duration-150 flex items-center gap-1.5',
            activeTab === tab.id
              ? 'bg-white dark:bg-[#18243A] text-slate-900 dark:text-white shadow-2xs border border-slate-200/80 dark:border-[#263449]'
              : 'text-slate-500 dark:text-slate-400 hover:text-slate-800 dark:hover:text-slate-200 hover:bg-slate-200/50 dark:hover:bg-slate-800/50',
          ]"
        >
          <component :is="tab.icon" class="w-3.5 h-3.5" />
          <span>{{ tab.label }}</span>
        </button>
      </div>

      <!-- Scrollable Form Body -->
      <div class="flex-1 overflow-y-auto p-5 sm:p-7 space-y-6 bg-slate-50/50 dark:bg-[#0B1120]">
        <!-- General Error Banner -->
        <div
          v-if="generalError"
          class="p-4 bg-rose-50 dark:bg-rose-950/40 border border-rose-200 dark:border-rose-900/60 rounded-2xl flex items-start gap-3"
        >
          <div class="p-1 bg-rose-100 dark:bg-rose-900/60 rounded-full text-rose-600 dark:text-rose-400 shrink-0">
            <AlertCircle class="w-4 h-4" />
          </div>
          <div class="flex-1 min-w-0">
            <h4 class="text-xs font-bold text-rose-800 dark:text-rose-200">Registration Error</h4>
            <p class="text-xs text-rose-700 dark:text-rose-300 mt-0.5 break-words">{{ generalError }}</p>
          </div>
          <button
            @click="generalError = ''"
            class="text-rose-400 hover:text-rose-600 p-1 rounded-lg"
          >
            <X class="w-4 h-4" />
          </button>
        </div>

        <!-- TAB 1: Basic & Contact Info -->
        <div
          v-show="activeTab === 'basic'"
          class="bg-white dark:bg-[#111C30] p-5 sm:p-6 rounded-2xl border border-slate-200/80 dark:border-[#263449] shadow-xs space-y-5"
        >
          <div class="flex items-center gap-2 font-bold text-slate-900 dark:text-white text-xs uppercase tracking-wider pb-3 border-b border-slate-100 dark:border-[#1E293B]">
            <User class="w-4 h-4 text-indigo-600 dark:text-indigo-400" />
            <span>Personal & Contact Information</span>
          </div>

          <div class="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-5">
            <!-- Full Name -->
            <div>
              <label class="block text-xs font-bold text-slate-700 dark:text-slate-300 uppercase tracking-wider mb-1.5">
                Full Name <span class="text-rose-500">*</span>
              </label>
              <input
                v-model="newStudent.name"
                :disabled="isAdding"
                placeholder="Student legal full name"
                class="w-full px-3.5 py-2.5 border rounded-xl text-xs sm:text-sm font-medium bg-white dark:bg-[#18243A] text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-600 transition"
                :class="formErrors.name ? 'border-rose-300 bg-rose-50/50' : 'border-slate-300 dark:border-[#263449]'"
              />
              <p v-if="formErrors.name" class="text-xs text-rose-600 dark:text-rose-400 mt-1 flex items-center gap-1">
                <AlertCircle class="w-3.5 h-3.5" /> {{ formErrors.name }}
              </p>
            </div>

            <!-- Roll Number -->
            <div>
              <label class="block text-xs font-bold text-slate-700 dark:text-slate-300 uppercase tracking-wider mb-1.5">
                Roll Number <span class="text-rose-500">*</span>
              </label>
              <input
                v-model="newStudent.rollNumber"
                :disabled="isAdding"
                placeholder="e.g. 101, A-12"
                class="w-full px-3.5 py-2.5 border rounded-xl text-xs sm:text-sm font-mono font-medium bg-white dark:bg-[#18243A] text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-600 transition"
                :class="formErrors.rollNumber ? 'border-rose-300 bg-rose-50/50' : 'border-slate-300 dark:border-[#263449]'"
              />
              <p v-if="formErrors.rollNumber" class="text-xs text-rose-600 dark:text-rose-400 mt-1 flex items-center gap-1">
                <AlertCircle class="w-3.5 h-3.5" /> {{ formErrors.rollNumber }}
              </p>
            </div>

            <!-- Email Address -->
            <div>
              <label class="block text-xs font-bold text-slate-700 dark:text-slate-300 uppercase tracking-wider mb-1.5">
                Email Address <span class="text-slate-400 font-normal lowercase">(optional)</span>
              </label>
              <input
                v-model="newStudent.email"
                type="email"
                :disabled="isAdding"
                placeholder="student@school.edu"
                class="w-full px-3.5 py-2.5 border rounded-xl text-xs sm:text-sm font-medium bg-white dark:bg-[#18243A] text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-600 transition"
                :class="formErrors.email ? 'border-rose-300 bg-rose-50/50' : 'border-slate-300 dark:border-[#263449]'"
              />
              <p v-if="formErrors.email" class="text-xs text-rose-600 dark:text-rose-400 mt-1 flex items-center gap-1">
                <AlertCircle class="w-3.5 h-3.5" /> {{ formErrors.email }}
              </p>
            </div>

            <!-- Primary Phone -->
            <div>
              <label class="block text-xs font-bold text-slate-700 dark:text-slate-300 uppercase tracking-wider mb-1.5">
                Primary Phone <span class="text-rose-500">*</span>
              </label>
              <input
                v-model="newStudent.phone"
                :disabled="isAdding"
                placeholder="10-digit mobile number"
                class="w-full px-3.5 py-2.5 border rounded-xl text-xs sm:text-sm font-mono font-medium bg-white dark:bg-[#18243A] text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-600 transition"
                :class="formErrors.phone ? 'border-rose-300 bg-rose-50/50' : 'border-slate-300 dark:border-[#263449]'"
              />
              <p v-if="formErrors.phone" class="text-xs text-rose-600 dark:text-rose-400 mt-1 flex items-center gap-1">
                <AlertCircle class="w-3.5 h-3.5" /> {{ formErrors.phone }}
              </p>
            </div>

            <!-- Secondary Phone -->
            <div>
              <label class="block text-xs font-bold text-slate-700 dark:text-slate-300 uppercase tracking-wider mb-1.5">
                Parent / Guardian Phone <span class="text-slate-400 font-normal lowercase">(optional)</span>
              </label>
              <input
                v-model="newStudent.secondaryPhone"
                :disabled="isAdding"
                placeholder="Emergency contact"
                class="w-full px-3.5 py-2.5 border border-slate-300 dark:border-[#263449] rounded-xl text-xs sm:text-sm font-mono font-medium bg-white dark:bg-[#18243A] text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-600 transition"
              />
              <p v-if="formErrors.secondaryPhone" class="text-xs text-rose-600 dark:text-rose-400 mt-1 flex items-center gap-1">
                <AlertCircle class="w-3.5 h-3.5" /> {{ formErrors.secondaryPhone }}
              </p>
            </div>

            <!-- Date of Birth -->
            <div>
              <label class="block text-xs font-bold text-slate-700 dark:text-slate-300 uppercase tracking-wider mb-1.5">
                Date of Birth <span class="text-slate-400 font-normal lowercase">(optional)</span>
              </label>
              <input
                type="date"
                v-model="newStudent.dob"
                :disabled="isAdding"
                :max="new Date().toISOString().split('T')[0]"
                class="w-full px-3.5 py-2.5 border border-slate-300 dark:border-[#263449] rounded-xl text-xs sm:text-sm font-medium bg-white dark:bg-[#18243A] text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-600 transition"
              />
            </div>
          </div>
        </div>

        <!-- TAB 2: Academic & Enrollment -->
        <div
          v-show="activeTab === 'academic'"
          class="bg-white dark:bg-[#111C30] p-5 sm:p-6 rounded-2xl border border-slate-200/80 dark:border-[#263449] shadow-xs space-y-5"
        >
          <div class="flex items-center gap-2 font-bold text-slate-900 dark:text-white text-xs uppercase tracking-wider pb-3 border-b border-slate-100 dark:border-[#1E293B]">
            <GraduationCap class="w-4 h-4 text-emerald-600 dark:text-emerald-400" />
            <span>Academic & Enrollment Information</span>
          </div>

          <div class="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-5">
            <!-- Class Selection -->
            <div>
              <label class="block text-xs font-bold text-slate-700 dark:text-slate-300 uppercase tracking-wider mb-1.5">
                Class / Standard <span class="text-rose-500">*</span>
              </label>
              <select
                v-model="newStudent.class"
                :disabled="isAdding"
                class="w-full px-3.5 py-2.5 border rounded-xl text-xs sm:text-sm font-medium bg-white dark:bg-[#18243A] text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-600 transition"
                :class="formErrors.class ? 'border-rose-300 bg-rose-50/50' : 'border-slate-300 dark:border-[#263449]'"
              >
                <option value="" disabled selected hidden>Select Class</option>
                <option
                  v-for="cls in filteredClasses"
                  :key="cls._id"
                  :value="cls._id"
                >
                  {{ cls.name }}{{ cls.section ? ` - ${cls.section}` : "" }}
                </option>
              </select>
              <p v-if="formErrors.class" class="text-xs text-rose-600 dark:text-rose-400 mt-1 flex items-center gap-1">
                <AlertCircle class="w-3.5 h-3.5" /> {{ formErrors.class }}
              </p>
            </div>

            <!-- Status -->
            <div>
              <label class="block text-xs font-bold text-slate-700 dark:text-slate-300 uppercase tracking-wider mb-1.5">
                Status <span class="text-rose-500">*</span>
              </label>
              <select
                v-model="newStudent.status"
                :disabled="isAdding"
                class="w-full px-3.5 py-2.5 border border-slate-300 dark:border-[#263449] rounded-xl text-xs sm:text-sm font-medium bg-white dark:bg-[#18243A] text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-600 transition"
              >
                <option
                  v-for="status in filteredStatuses"
                  :key="status.value"
                  :value="status.value"
                >
                  {{ status.label }}
                </option>
              </select>
            </div>

            <!-- Enrollment Date -->
            <div>
              <label class="block text-xs font-bold text-slate-700 dark:text-slate-300 uppercase tracking-wider mb-1.5">
                Enrollment Date <span class="text-rose-500">*</span>
              </label>
              <input
                type="date"
                v-model="newStudent.enrollmentDate"
                :disabled="isAdding"
                :max="new Date().toISOString().split('T')[0]"
                class="w-full px-3.5 py-2.5 border border-slate-300 dark:border-[#263449] rounded-xl text-xs sm:text-sm font-medium bg-white dark:bg-[#18243A] text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-600 transition"
              />
              <p v-if="formErrors.enrollmentDate" class="text-xs text-rose-600 dark:text-rose-400 mt-1 flex items-center gap-1">
                <AlertCircle class="w-3.5 h-3.5" /> {{ formErrors.enrollmentDate }}
              </p>
            </div>

            <!-- Passout fields if applicable -->
            <template v-if="isPassoutSelectedInForm && newStudent.status === 'passout'">
              <div>
                <label class="block text-xs font-bold text-slate-700 dark:text-slate-300 uppercase tracking-wider mb-1.5">
                  Session <span class="text-slate-400 font-normal lowercase">(optional)</span>
                </label>
                <input
                  v-model="newStudent.session"
                  :disabled="isAdding"
                  placeholder="e.g. Winter / Summer"
                  class="w-full px-3.5 py-2.5 border border-slate-300 dark:border-[#263449] rounded-xl text-xs sm:text-sm font-medium bg-white dark:bg-[#18243A] text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-600 transition"
                />
              </div>

              <div>
                <label class="block text-xs font-bold text-slate-700 dark:text-slate-300 uppercase tracking-wider mb-1.5">
                  Graduation Year
                </label>
                <input
                  v-model="newStudent.year"
                  :disabled="isAdding"
                  type="number"
                  placeholder="e.g. 2024"
                  class="w-full px-3.5 py-2.5 border border-slate-300 dark:border-[#263449] rounded-xl text-xs sm:text-sm font-medium bg-white dark:bg-[#18243A] text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-600 transition"
                />
              </div>
            </template>
          </div>
        </div>

        <!-- TAB 3: Address & Location -->
        <div
          v-show="activeTab === 'address'"
          class="bg-white dark:bg-[#111C30] p-5 sm:p-6 rounded-2xl border border-slate-200/80 dark:border-[#263449] shadow-xs space-y-5"
        >
          <div class="flex items-center gap-2 font-bold text-slate-900 dark:text-white text-xs uppercase tracking-wider pb-3 border-b border-slate-100 dark:border-[#1E293B]">
            <MapPin class="w-4 h-4 text-amber-600 dark:text-amber-400" />
            <span>Address Details</span>
          </div>

          <div class="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-5">
            <!-- Pincode -->
            <div>
              <label class="block text-xs font-bold text-slate-700 dark:text-slate-300 uppercase tracking-wider mb-1.5">
                Pincode <span class="text-rose-500">*</span>
              </label>
              <input
                v-model="newStudent.pincode"
                :disabled="isAdding"
                placeholder="6-digit pincode"
                maxlength="6"
                class="w-full px-3.5 py-2.5 border rounded-xl text-xs sm:text-sm font-mono font-medium bg-white dark:bg-[#18243A] text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-600 transition"
                :class="formErrors.pincode ? 'border-rose-300 bg-rose-50/50' : 'border-slate-300 dark:border-[#263449]'"
              />
              <p v-if="formErrors.pincode" class="text-xs text-rose-600 dark:text-rose-400 mt-1 flex items-center gap-1">
                <AlertCircle class="w-3.5 h-3.5" /> {{ formErrors.pincode }}
              </p>
            </div>

            <!-- City / Area -->
            <div>
              <label class="block text-xs font-bold text-slate-700 dark:text-slate-300 uppercase tracking-wider mb-1.5">
                City / Area <span class="text-slate-400 font-normal lowercase">(optional)</span>
              </label>
              <input
                v-model="newStudent.city"
                :disabled="isAdding"
                placeholder="City name"
                class="w-full px-3.5 py-2.5 border border-slate-300 dark:border-[#263449] rounded-xl text-xs sm:text-sm font-medium bg-white dark:bg-[#18243A] text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-600 transition"
              />
            </div>

            <!-- District -->
            <div>
              <label class="block text-xs font-bold text-slate-700 dark:text-slate-300 uppercase tracking-wider mb-1.5">
                District <span class="text-slate-400 font-normal lowercase">(optional)</span>
              </label>
              <input
                v-model="newStudent.district"
                :disabled="isAdding"
                placeholder="District or zone"
                class="w-full px-3.5 py-2.5 border border-slate-300 dark:border-[#263449] rounded-xl text-xs sm:text-sm font-medium bg-white dark:bg-[#18243A] text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-600 transition"
              />
            </div>

            <!-- State -->
            <div>
              <label class="block text-xs font-bold text-slate-700 dark:text-slate-300 uppercase tracking-wider mb-1.5">
                State <span class="text-slate-400 font-normal lowercase">(optional)</span>
              </label>
              <input
                v-model="newStudent.state"
                :disabled="isAdding"
                placeholder="State / province"
                class="w-full px-3.5 py-2.5 border border-slate-300 dark:border-[#263449] rounded-xl text-xs sm:text-sm font-medium bg-white dark:bg-[#18243A] text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-600 transition"
              />
            </div>
          </div>
        </div>

        <!-- TAB 4: Photo & Documents -->
        <div
          v-show="activeTab === 'documents'"
          class="space-y-5"
        >
          <!-- Photo Upload Card -->
          <div class="bg-white dark:bg-[#111C30] p-5 sm:p-6 rounded-2xl border border-slate-200/80 dark:border-[#263449] shadow-xs space-y-4">
            <div class="flex items-center gap-2 font-bold text-slate-900 dark:text-white text-xs uppercase tracking-wider pb-3 border-b border-slate-100 dark:border-[#1E293B]">
              <Camera class="w-4 h-4 text-indigo-600 dark:text-indigo-400" />
              <span>Student Photo</span>
            </div>

            <div class="flex flex-col sm:flex-row items-center gap-5">
              <div class="w-20 h-20 sm:w-24 sm:h-24 rounded-2xl overflow-hidden ring-4 ring-slate-100 dark:ring-slate-800 shadow-md shrink-0 bg-slate-100 dark:bg-slate-800 flex items-center justify-center">
                <img
                  v-if="newStudent.photo"
                  :src="newStudent.photo"
                  alt="Student Photo"
                  class="w-full h-full object-cover"
                />
                <User v-else class="w-10 h-10 text-slate-400" />
              </div>

              <div class="flex-1 space-y-2 w-full text-center sm:text-left">
                <div class="flex items-center gap-2 justify-center sm:justify-start">
                  <label class="cursor-pointer inline-flex items-center gap-1.5 px-3.5 py-2 bg-indigo-50 dark:bg-indigo-950/60 hover:bg-indigo-100 text-indigo-700 dark:text-indigo-300 rounded-xl text-xs font-semibold border border-indigo-200 dark:border-indigo-800 transition">
                    <Upload class="w-3.5 h-3.5" />
                    <span>Select Photo</span>
                    <input
                      type="file"
                      accept="image/*"
                      @change="handlePhotoUpload"
                      :disabled="isAdding"
                      class="hidden"
                    />
                  </label>
                  <button
                    v-if="newStudent.photo"
                    type="button"
                    @click="newStudent.photo = ''"
                    :disabled="isAdding"
                    class="px-3 py-2 text-rose-600 dark:text-rose-400 hover:bg-rose-50 dark:hover:bg-rose-950/50 rounded-xl text-xs font-semibold transition"
                  >
                    Remove
                  </button>
                </div>
                <p class="text-[11px] text-slate-400 dark:text-slate-500">
                  Upload a clear portrait (JPG, PNG, max 1MB)
                </p>
                <p v-if="formErrors.photo" class="text-xs text-rose-600 dark:text-rose-400">
                  {{ formErrors.photo }}
                </p>
              </div>
            </div>
          </div>

          <!-- Documentation Records Card -->
          <div class="bg-white dark:bg-[#111C30] p-5 sm:p-6 rounded-2xl border border-slate-200/80 dark:border-[#263449] shadow-xs space-y-4">
            <div class="flex items-center gap-2 font-bold text-slate-900 dark:text-white text-xs uppercase tracking-wider pb-3 border-b border-slate-100 dark:border-[#1E293B]">
              <FileText class="w-4 h-4 text-violet-600 dark:text-violet-400" />
              <span>Identity & Academic Verification Files</span>
            </div>

            <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
              <template
                v-for="doc in [
                  'aadhaarCard',
                  'birthCertificate',
                  'transferCertificate',
                  'marksheet',
                ]"
                :key="doc"
              >
                <div class="p-4 rounded-xl border border-slate-200/80 dark:border-[#263449] bg-slate-50/50 dark:bg-[#0F172A] space-y-2">
                  <div class="flex items-center justify-between">
                    <span class="text-xs font-bold text-slate-800 dark:text-white capitalize">
                      {{ doc.replace(/([A-Z])/g, " $1") }}
                    </span>
                    <span
                      v-if="newStudent[doc]"
                      class="inline-flex items-center gap-1 text-[10px] font-semibold text-emerald-600 dark:text-emerald-400 bg-emerald-50 dark:bg-emerald-950/60 px-2 py-0.5 rounded-full"
                    >
                      <CheckCircle class="w-3 h-3" /> Ready
                    </span>
                  </div>
                  <input
                    type="file"
                    accept=".pdf,.jpg,.jpeg,.png"
                    @change="handleDocumentUpload($event, doc)"
                    :disabled="isAdding"
                    class="w-full text-xs text-slate-500 file:mr-2.5 file:py-1.5 file:px-3 file:rounded-lg file:border-0 file:text-xs file:font-semibold file:bg-indigo-50 file:text-indigo-700 hover:file:bg-indigo-100"
                  />
                  <p v-if="formErrors[doc]" class="text-xs text-rose-600 dark:text-rose-400">
                    {{ formErrors[doc] }}
                  </p>
                </div>
              </template>
            </div>
            <p class="text-[11px] text-slate-400 dark:text-slate-500">
              Supported formats: PDF, JPG, PNG (Max 2MB per file)
            </p>
          </div>
        </div>
      </div>

      <!-- Sticky Modal Actions Footer -->
      <div
        class="p-4 sm:p-5 border-t border-slate-200/80 dark:border-[#263449] bg-white dark:bg-[#111C30] flex items-center justify-between shrink-0"
      >
        <span class="text-[11px] text-slate-400 dark:text-slate-500 hidden sm:inline">
          Required fields are marked with <span class="text-rose-500 font-bold">*</span>
        </span>
        <div class="flex items-center gap-2.5 w-full sm:w-auto justify-end">
          <button
            type="button"
            @click="closeAddModal"
            :disabled="isAdding"
            class="px-4 py-2.5 rounded-xl text-xs font-semibold text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800 transition"
          >
            Cancel
          </button>
          <button
            type="button"
            @click="addStudent"
            :disabled="isAdding"
            class="inline-flex items-center justify-center gap-2 px-5 py-2.5 bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl text-xs font-semibold shadow-sm transition disabled:opacity-50"
          >
            <AppLoader
              v-if="isAdding"
              variant="button"
              class="w-3.5 h-3.5 text-white"
            />
            <Save v-else class="w-3.5 h-3.5" />
            <span>{{ isAdding ? "Enrolling..." : "Enroll Student" }}</span>
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
  import { ref, watch, computed, onMounted } from "vue";
  import { useStore } from "vuex";
  import {
    X,
    Save,
    User,
    GraduationCap,
    Plus,
    AlertCircle,
    Camera,
    FileText,
    CheckCircle,
    MapPin,
    Upload,
  } from "lucide-vue-next";
  import { useGlobalToast } from "@/utils/useGlobalToast";
  import Search from "@/components/Students/Search/Search.vue";
  import StatusFilter from "@/components/Students/StatusFilter/StatusFilter.vue";
  import YearPicker from "@/components/Students/PassOutFilter/YearPicker.vue";

  const store = useStore();
  const { showToast: showGlobalToast } = useGlobalToast();

  const props = defineProps({
    bannerGoClicked: {
      type: Boolean,
      default: false,
    },
  });

  const emit = defineEmits(["selected-class", "search-text", "selected-year"]);

  const showAddModal = ref(false);
  const isAdding = ref(false);
  const generalError = ref("");
  const activeTab = ref("basic");

  const formTabs = [
    { id: "basic", label: "Personal & Contact", icon: User },
    { id: "academic", label: "Academic Info", icon: GraduationCap },
    { id: "address", label: "Address & Location", icon: MapPin },
    { id: "documents", label: "Photo & Documents", icon: FileText },
  ];

  const classes = computed(() => store.getters.allClasses || []);
  const TotalStudents = computed(
    () => store.getters.getOnboardingTotalStudents || 0,
  );
  const selectedClass = ref(null);
  const selectedYear = ref("");
  const searchText = ref("");
  const phoneRegex = /^[6-9]\d{9}$/;

  function emitSelectedClass(cls) {
    selectedClass.value = cls;
    emit("selected-class", cls || null);
  }

  const isPassoutClass = computed(() => {
    if (!selectedClass.value) return false;
    return selectedClass.value.name?.toLowerCase() === "passout";
  });

  function emitSelectedYear(year) {
    if (isPassoutClass.value) {
      selectedYear.value = year;
      emit("selected-year", year);
    } else {
      selectedYear.value = "";
      emit("selected-year", "");
    }
  }

  watch(isPassoutClass, (val) => {
    if (!val) {
      selectedYear.value = "";
      emit("selected-year", "");
    }
  });

  watch(selectedClass, () => {
    emitSearchText("");
    searchText.value = "";
  });

  const formErrors = ref({});
  const newStudent = ref({
    rollNumber: "",
    name: "",
    email: "",
    class: "",
    phone: "",
    secondaryPhone: "",
    state: "",
    district: "",
    city: "",
    pincode: "",
    status: "active",
    enrollmentDate: "",
    photo: "",
    aadhaarCard: "",
    birthCertificate: "",
    transferCertificate: "",
    dob: "",
    marksheet: "",
    session: "",
    year: "",
  });

  const emitSearchText = (val) => emit("search-text", val);

  const closeAddModal = () => {
    showAddModal.value = false;
    newStudent.value = {
      rollNumber: "",
      name: "",
      email: "",
      class: "",
      phone: "",
      secondaryPhone: "",
      state: "",
      district: "",
      city: "",
      pincode: "",
      status: "active",
      enrollmentDate: "",
      photo: "",
      dob: "",
      aadhaarCard: "",
      birthCertificate: "",
      transferCertificate: "",
      marksheet: "",
      session: "",
      year: "",
    };
    formErrors.value = {};
    generalError.value = "";
  };

  const handlePhotoUpload = (event) => {
    const file = event.target.files[0];
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      formErrors.value.photo = "Only image files are allowed.";
      return;
    }
    if (file.size > 1024 * 1024) {
      formErrors.value.photo = "Photo must be less than 1MB.";
      return;
    }
    newStudent.value.photo = file;
  };

  const handleDocumentUpload = (event, documentType) => {
    const file = event.target.files[0];
    if (!file) return;
    const allowedTypes = [
      "application/pdf",
      "image/jpeg",
      "image/jpg",
      "image/png",
    ];
    if (!allowedTypes.includes(file.type)) {
      formErrors.value[documentType] = "Invalid file type.";
      return;
    }
    if (file.size > 2 * 1024 * 1024) {
      formErrors.value[documentType] = "File must be under 2MB.";
      return;
    }
    newStudent.value[documentType] = file;
  };

  const refreshStudents = async () => {
    try {
      await store.dispatch("fetchStudents", {
        page: 1,
        limit: 20,
        search: "",
      });
      await store.dispatch("fetchTotalStudentCountOnboarding");
    } catch (error) {
      console.error("Error refreshing students:", error);
    }
  };

  const addStudent = async () => {
    formErrors.value = {};
    generalError.value = "";

    if (!newStudent.value.name?.trim()) {
      formErrors.value.name = "Student name is required";
      showGlobalToast("error", "Student name is required");
      activeTab.value = "basic";
      return;
    }

    if (!newStudent.value.rollNumber?.trim()) {
      formErrors.value.rollNumber = "Roll number is required";
      showGlobalToast("error", "Roll number is required");
      activeTab.value = "basic";
      return;
    }

    if (!newStudent.value.class) {
      formErrors.value.class = "Class selection is required";
      showGlobalToast("error", "Class selection is required");
      activeTab.value = "academic";
      return;
    }

    const selectedCls = classes.value.find(
      (c) => c._id === newStudent.value.class,
    );
    const isPassoutCls = selectedCls?.name?.toLowerCase() === "passout";
    const isPassoutStat = newStudent.value.status === "passout";

    if (isPassoutCls && !isPassoutStat) {
      formErrors.value.status = "Status must be 'Passed Out' when class is 'Passout'";
      showGlobalToast("error", "Status must be 'Passed Out' when class is 'Passout'");
      activeTab.value = "academic";
      return;
    }

    if (isPassoutStat && !isPassoutCls) {
      formErrors.value.class = "Class must be 'Passout' when status is 'Passed Out'";
      showGlobalToast("error", "Class must be 'Passout' when status is 'Passed Out'");
      activeTab.value = "academic";
      return;
    }

    if (!newStudent.value.phone?.trim()) {
      formErrors.value.phone = "Primary phone number is required";
      showGlobalToast("error", "Primary phone number is required");
      activeTab.value = "basic";
      return;
    }

    if (!phoneRegex.test(newStudent.value.phone)) {
      formErrors.value.phone = "Enter a valid 10-digit mobile number";
      showGlobalToast("error", "Enter a valid primary mobile number");
      activeTab.value = "basic";
      return;
    }

    if (newStudent.value.secondaryPhone) {
      if (!phoneRegex.test(newStudent.value.secondaryPhone)) {
        formErrors.value.secondaryPhone = "Enter a valid 10-digit secondary mobile number";
        showGlobalToast("error", "Invalid secondary mobile number");
        activeTab.value = "basic";
        return;
      }

      if (newStudent.value.secondaryPhone === newStudent.value.phone) {
        formErrors.value.secondaryPhone = "Secondary phone cannot be same as primary phone";
        showGlobalToast("error", "Secondary phone cannot be same as primary phone");
        activeTab.value = "basic";
        return;
      }
    }

    if (!/^\d{6}$/.test(newStudent.value.pincode)) {
      formErrors.value.pincode = "Valid 6-digit pincode is required";
      showGlobalToast("error", "Valid 6-digit pincode is required");
      activeTab.value = "address";
      return;
    }

    isAdding.value = true;
    try {
      const formData = new FormData();
      const trimmedName = newStudent.value.name.trim();
      const finalEmail = newStudent.value.email?.trim() || "";

      formData.append("name", trimmedName);
      formData.append("email", finalEmail);
      formData.append("role", "student");
      formData.append("phone", newStudent.value.phone || "");

      if (newStudent.value.secondaryPhone) {
        formData.append("secondaryPhone", newStudent.value.secondaryPhone.trim());
      }

      formData.append("class", newStudent.value.class);
      formData.append("rollNumber", newStudent.value.rollNumber.trim());
      formData.append("status", newStudent.value.status || "active");
      formData.append(
        "enrollmentDate",
        newStudent.value.enrollmentDate || new Date().toISOString(),
      );
      formData.append("dob", newStudent.value.dob || null);

      if (newStudent.value.photo instanceof File) {
        formData.append("photo", newStudent.value.photo);
      }
      if (newStudent.value.aadhaarCard instanceof File) {
        formData.append("aadhaarCard", newStudent.value.aadhaarCard);
      }
      if (newStudent.value.birthCertificate instanceof File) {
        formData.append("birthCertificate", newStudent.value.birthCertificate);
      }
      if (newStudent.value.transferCertificate instanceof File) {
        formData.append("transferCertificate", newStudent.value.transferCertificate);
      }
      if (newStudent.value.marksheet instanceof File) {
        formData.append("marksheet", newStudent.value.marksheet);
      }

      formData.append("pincode", newStudent.value.pincode);
      if (newStudent.value.state) formData.append("state", newStudent.value.state);
      if (newStudent.value.district) formData.append("subDistrict", newStudent.value.district);
      if (newStudent.value.city) formData.append("city", newStudent.value.city);

      if (isPassoutClass.value && newStudent.value.status === "passout") {
        if (newStudent.value.session) formData.append("session", newStudent.value.session);
        if (newStudent.value.year) formData.append("passoutYear", newStudent.value.year);
      }

      await store.dispatch("addStudent", formData);
      showGlobalToast("success", "Student enrolled successfully!");
      await refreshStudents();
      closeAddModal();
    } catch (error) {
      console.error("Add student error:", error);
      if (error.response?.data?.errors) {
        formErrors.value = error.response.data.errors;
        const firstErrorKey = Object.keys(formErrors.value)[0];
        const firstErrorMessage = formErrors.value[firstErrorKey];
        showGlobalToast("error", firstErrorMessage);
      } else {
        const message = error.response?.data?.message || "Server error.";
        generalError.value = message;
        showGlobalToast("error", message);
      }
    } finally {
      isAdding.value = false;
    }
  };

  const selectedFormClass = computed(() => {
    return classes.value.find((c) => c._id === newStudent.value.class) || null;
  });

  const isPassoutSelectedInForm = computed(() => {
    return selectedFormClass.value?.name?.toLowerCase() === "passout";
  });

  watch(isPassoutSelectedInForm, (val) => {
    if (val) {
      newStudent.value.status = "passout";
    }
  });

  watch(
    () => newStudent.value.status,
    (val) => {
      if (val === "passout") {
        const passoutClass = classes.value.find(
          (c) => c.name?.toLowerCase() === "passout",
        );
        if (passoutClass) {
          newStudent.value.class = passoutClass._id;
        }
      }
    },
  );

  onMounted(() => {
    store.dispatch("fetchAllClasses");
  });

  watch(
    TotalStudents,
    (newVal) => {
      if (newVal === 0) {
        showAddModal.value = true;
      }
    },
    { immediate: true },
  );

  const filteredClasses = computed(() => {
    return classes.value.filter((cls) => {
      if (TotalStudents.value === 0 && cls.name?.toLowerCase() === "passout") {
        return false;
      }
      return true;
    });
  });

  const filteredStatuses = computed(() => {
    const statuses = [{ label: "Active", value: "active" }];
    if (TotalStudents.value > 0) {
      statuses.push({ label: "Passed Out", value: "passout" });
    }
    return statuses;
  });

  watch(
    () => props.bannerGoClicked,
    (newVal) => {
      if (newVal) {
        showAddModal.value = true;
      }
    },
  );
</script>

<style scoped>
  @keyframes fade-in {
    from {
      opacity: 0;
      transform: scale(0.97);
    }
    to {
      opacity: 1;
      transform: scale(1);
    }
  }
  .animate-fade-in {
    animation: fade-in 0.2s cubic-bezier(0.16, 1, 0.3, 1);
  }
</style>
